import { Alert, AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Slide, Snackbar, TextField, Typography } from '@mui/material'
import { FormatListNumberedOutlined } from '@mui/icons-material';
import { useEffect, useState } from 'react'
import { CreateTenantRequest, DocumentationStagesSaveError, RenameTenantRequest, SaveDocumentationStageItem, SaveDocumentationStagesResponse, SaveTenantDocumentationStagesRequest, TenantDTO, UsersGateApiClient } from '../Api/ApiServerHooks';
import MuiDialogHeader from '../Shared/MuiDialogHeader';
import { useGateApi } from '../Api/useGateApi';
import { useTranslation } from 'react-i18next';
import DocumentationStagesEditor, { EditableDocumentationStage, hasStageNameErrors } from './DocumentationStagesEditor';

interface CreateTenantDialogProps {
    isOpen: boolean;
    onClose: (refresh: boolean, id?: number) => void;
    tenant?: TenantDTO;
}

export default function CreateTenantDialog(props: Readonly<CreateTenantDialogProps>) {
    const { isOpen, onClose, tenant } = props;
    const { t } = useTranslation();
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [newName, setNewName] = useState('');
    const [rsdApiKey, setRsdApiKey] = useState('');
    const [openCreateError, setOpenCreateError] = useState(false);
    /** Stupně PD tenantu - šablona, kterou převezme každý nově založený datový standard. Uloží se s tenantem. */
    const [stages, setStages] = useState<EditableDocumentationStage[]>([]);
    const [stagesLoading, setStagesLoading] = useState(false);
    const [stagesError, setStagesError] = useState<string | null>(null);
    const [stagesDialogOpen, setStagesDialogOpen] = useState(false);
    const { authorizedApi } = useGateApi(UsersGateApiClient);
    const stagesInvalid = hasStageNameErrors(stages, t);

    useEffect(() => {
        if (tenant?.name) {
            setNewName(tenant.name);
            setRsdApiKey(tenant?.rsdApiKey ?? '');
        }
        else {
            setNewName('');
            setRsdApiKey('');
        }

        setButtonDisabled(false);
        setRefresh(false);
    }, []);

    useEffect(() => {
        if (tenant?.name) {
            setNewName(tenant.name);
            setRsdApiKey(tenant?.rsdApiKey ?? '');
        }
        else {
            setNewName('');
            setRsdApiKey('');
        }
    }, [tenant]);

    useEffect(() => {
        if (!isOpen)
            return;

        setStagesError(null);
        setStagesDialogOpen(false);
        setStages([]);

        // nový tenant začíná bez stupňů - uživatel si přidá ty, které chce
        if (tenant?.id) {
            setStagesLoading(true);
            authorizedApi?.getTenantDocumentationStages(tenant.id).then((res) => {
                const sorted = [...(res.stages ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                setStages(sorted.map(s => ({ key: `stage-${s.id}`, id: s.id ?? 0, name: s.name ?? '' })));
            }).catch(() => {
                setStagesError(t('DocumentationStages.ErrorLoad'));
            }).finally(() => setStagesLoading(false));
        }
    }, [isOpen, tenant?.id]);

    const getSaveErrorText = (res: SaveDocumentationStagesResponse): string => {
        const names = (res.conflictingNames ?? []).join(', ');

        switch (res.error) {
            case DocumentationStagesSaveError.EmptyName:
                return t('DocumentationStages.ErrorEmptyName');
            case DocumentationStagesSaveError.DuplicateName:
                return t('DocumentationStages.ErrorDuplicateName', { names: names });
            case DocumentationStagesSaveError.NameTooLong:
                return t('DocumentationStages.ErrorNameTooLong', { names: names });
            default:
                return t('DocumentationStages.ErrorSave');
        }
    };

    const saveStages = async (tenantId: number): Promise<boolean> => {
        if (!authorizedApi)
            return false;

        const res = await authorizedApi.saveTenantDocumentationStages(new SaveTenantDocumentationStagesRequest({
            tenantId: tenantId,
            stages: stages.map(s => new SaveDocumentationStageItem({ id: s.id, name: s.name.trim() }))
        }));

        if (!res.ok)
            setStagesError(getSaveErrorText(res));

        return res.ok ?? false;
    };

    function TransitionLeft(props: any) {
        return <Slide {...props} direction='left' />;
    }

    const handleClose = (event: any) => {
        setOpenCreateError(false);
    };

    return (
        <Dialog fullWidth maxWidth='sm' onClose={() => onClose(refresh)} open={isOpen}>
            {tenant && <MuiDialogHeader showCloseText title={t('Administration.EditTenant')} onClose={() => onClose(refresh)} />}
            {!tenant && <MuiDialogHeader showCloseText title={t('Administration.AddTenant')} onClose={() => onClose(refresh)} />}
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '10px' }}>
                    <TextField
                        fullWidth
                        label={t('Administration.TenantName')}
                        size='small'
                        variant='outlined'
                        value={newName}
                        onChange={(e) => { setNewName(e.target.value); }} />
                    <TextField
                        fullWidth
                        label={t('Administration.RSDAPIKey')}
                        size='small'
                        variant='outlined'
                        value={rsdApiKey}
                        onChange={(e) => { setRsdApiKey(e.target.value); }} />
                    <Box>
                        <Button
                            variant='outlined'
                            fullWidth
                            color={stagesInvalid ? 'error' : 'primary'}
                            disabled={stagesLoading || buttonDisabled}
                            startIcon={stagesLoading ? <CircularProgress size={16} /> : <FormatListNumberedOutlined />}
                            onClick={() => setStagesDialogOpen(true)}>
                            {t('DocumentationStages.ManageButton')} ({stages.length})
                        </Button>
                        {stagesError && <Alert severity='error' sx={{ marginTop: '12px' }}>{stagesError}</Alert>}
                    </Box>
                    <Box style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Button variant='contained' disabled={!newName || buttonDisabled || stagesLoading || stagesInvalid} onClick={() => {
                            setButtonDisabled(true);
                            setStagesError(null);
                            if (tenant?.id) {
                                const tenantId = tenant.id;
                                authorizedApi?.renameTenant(new RenameTenantRequest({
                                    tenantId: tenantId,
                                    name: newName,
                                    rsdApiKey: rsdApiKey
                                })).then(async (e) => {
                                    if (e.ok) {
                                        setRefresh(true);
                                        if (await saveStages(tenantId))
                                            onClose(true, tenantId);
                                    }
                                    else {
                                        setOpenCreateError(true);
                                    }
                                }).catch(() => {
                                    setStagesError(t('DocumentationStages.ErrorSave'));
                                }).finally(() => setButtonDisabled(false));
                            }
                            else {
                                authorizedApi?.createTenant(new CreateTenantRequest({
                                    name: newName,
                                    rsdApiKey: rsdApiKey
                                })).then(async (e) => {
                                    if (e.ok) {
                                        setRefresh(true);
                                        // tenant už existuje; když se šablonu nepodaří uložit, zůstane bez stupňů
                                        if (e.tenantId && stages.length > 0)
                                            await saveStages(e.tenantId).catch(() => false);
                                        onClose(true, e.tenantId);
                                    }
                                    else {
                                        setOpenCreateError(true);
                                    }
                                }).finally(() => setButtonDisabled(false));
                            }
                        }}>{t('Save')}</Button>
                    </Box>
                </Box>

                <Dialog fullWidth maxWidth='sm' open={stagesDialogOpen} onClose={() => setStagesDialogOpen(false)}>
                    <MuiDialogHeader showCloseText title={t('DocumentationStages.Title')} onClose={() => setStagesDialogOpen(false)} />
                    <DialogContent>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', marginBottom: '8px' }}>
                            {t('DocumentationStages.TenantHint')} {t('DocumentationStages.TenantSaveHint')}
                        </Typography>
                        <DocumentationStagesEditor stages={stages} onChange={setStages} disabled={buttonDisabled} />
                    </DialogContent>
                    <DialogActions>
                        <Button variant='contained' disabled={stagesInvalid} onClick={() => setStagesDialogOpen(false)}>{t('DocumentationStages.Done')}</Button>
                    </DialogActions>
                </Dialog>

                <Box className="create-form-popup">
                    <Snackbar
                        TransitionComponent={TransitionLeft}
                        open={openCreateError}
                        autoHideDuration={3000}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }} >
                        <Alert onClose={handleClose} severity="warning">
                            <AlertTitle>{t('Administration.ProjectCreationError')}</AlertTitle>
                            {t('Administration.TenantExist')}
                        </Alert>
                    </Snackbar>
                </Box>
            </DialogContent>
        </Dialog>
    )
}
